import { Box, Button, Chip, Stack, Table, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import * as XLSX from "xlsx";

import {
  blackColor,
  blackShade05,
  blackShade19,
  blueColor04,
  greenShade16,
  greyColor33,
  greyColor9,
  whiteColor,
} from "../../configs/styles/muiThemes";
import {
  getCatalogReportsData,
  getCatalogReportsFilterData,
} from "../../reducers/report/reportSlice";
import InventoryComparisonTable from "./InventoryComparisonTable";
import ThreeDotLoader from "../../components/ThreeDotLoader";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import FilterCard from "../newCatalog/FilterCard";
import { dynamicSort } from "../../utils/helper";
import { getONDCCategories } from "../../reducers/businessPreview/businessPreviewSlice";

function InventoryAnalysisContent() {
  const [activeTab, setActiveTab] = useState("inventoryAnalysis");

  const [showONDCFilter, setShowONDCFilter] = useState(false);
  const [ondcCategoryList, setOndcCategoryList] = useState([]);
  const [ondcSubCatList, setOndcSubCatList] = useState([]);
  const [selectedOndcCategory, setSelectedOndcCategory] = useState("All");
  const [selectedOndcSubCat, setSelectedOndcSubCat] = useState("All");

  const [filterError, setFilterError] = useState("");

  const [excelLoading, setExcelLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const ondcFilterRef = useRef(null);

  const { activeBusinessInfo, ondcCategory } = useSelector(
    (state) => state.businessPreview
  );
  const { catalogData, catalogDataLoader, filterMessage } = useSelector(
    (state) => state.reports
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const businessUrl = sessionStorage.getItem("costboWEBURL");
    navigate(`/${businessUrl}/multiBranchPriceAnalysis`);
  };

  const formatCategory = (category, setValue, valueSelector) => {
    const sortedArray = category?.slice()?.sort(dynamicSort(valueSelector));
    const catList = sortedArray?.map((item) => {
      return { ...item, label: item?.[valueSelector] };
    });

    setValue([{ label: "All" }, ...catList]);
  };

  const handleOndcCategoryChange = (data) => {
    setSelectedOndcCategory(data);
    setSelectedOndcSubCat("All");

    if (data === "All") {
      setOndcSubCatList([]);
      return;
    }

    setFilterError("");

    const categoryData = ondcCategoryList?.filter((item) => {
      return item?.ondcValue === data;
    });

    formatCategory(
      categoryData?.[0]?.productSubCategory,
      setOndcSubCatList,
      "ondcSubValue"
    );
  };

  const handleOndcSubCategoryChange = (data) => {
    setSelectedOndcSubCat(data);
  };

  const handleOndcFilter = async () => {
    setFilterError("");
    if (ondcCategoryList?.length === 1) {
      setFilterError("ONDC category not assigned for any of the products");

      return;
    }
    if (selectedOndcCategory === "All") {
      setFilterError("Select the Main Category");

      return;
    }

    const businessId = sessionStorage.getItem("selectedBusinessId");
    const categoryData = ondcCategoryList?.filter((item) => {
      return item?.ondcValue === selectedOndcCategory;
    });

    const subCategoryData = ondcSubCatList?.filter((item) => {
      return item?.ondcSubValue === selectedOndcSubCat;
    });

    dispatch(
      getCatalogReportsFilterData({
        id: businessId,
        params: {
          ondcCode: categoryData?.[0]?.ondcCode,
          ondcSubCode: subCategoryData?.[0]?.ondcSubCode || "",
          pageSize: 500,
          page: 1,
        },
        masterBranch: activeBusinessInfo,
        selectedOndcCategory,
        selectedOndcSubCat,
      })
    );

    setShowONDCFilter(false);
  };

  const onOndcFilterClose = () => {
    setFilterError("");
    setShowONDCFilter(false);
    setSelectedOndcCategory("All");
    setSelectedOndcSubCat("All");
    setOndcSubCatList([]);
  };

  const clearAllOndcFilterData = () => {
    setSelectedOndcCategory("All");
    setSelectedOndcSubCat("All");
    setOndcSubCatList([]);
  };

  const clearFilter = () => {
    clearAllOndcFilterData();
    dispatch(getCatalogReportsData(activeBusinessInfo));
  };

  const onExcelDownload = () => {
    setExcelLoading(true);
    const fileName = `Inventory_Analysis_${moment().format("Do_MM_YYYY")}`;

    // Headers
    const headers = [
      ["Inventory Analysis"],
      [
        `${activeBusinessInfo?.name}`,
        `Report date: ${moment().format("Do MMM YYYY")}`,
      ],
      [],
    ];

    // Branch Headers with Merging
    const firstRow = ["", catalogData?.header?.[0]?.name || ""];
    const merges = [];
    let colIndex = 2; // Starting merge index

    catalogData?.header?.slice(1)?.forEach((branch) => {
      firstRow.push(branch?.name, "");
      merges.push({ s: { r: 3, c: colIndex }, e: { r: 3, c: colIndex + 1 } });
      colIndex += 2;
    });

    // Column Titles
    const secondRow = ["Sl No", "Product Name"];
    catalogData?.header?.slice(1)?.forEach(() => {
      secondRow.push("Availability", "Available Qty");
    });

    // Data Rows
    const data = catalogData?.bodyData?.map((item, index) => {
      const row = [
        index + 1,
        item?.productName,
        item?.headquarters?.availability?.[0]?.toUpperCase() || "-",
        item?.headquarters?.availableQuantity || "-",
      ];
      item?.branches.forEach((branch) => {
        row.push(
          branch?.availability?.[0]?.toUpperCase() || "-",
          branch?.availableQuantity || "-"
        );
      });
      return row;
    });

    // Combine Everything
    const finalData = [...headers, firstRow, secondRow, ...data];

    // Create Excel Sheet
    const worksheet = XLSX.utils.aoa_to_sheet(finalData);
    worksheet["!merges"] = merges; // Apply Merges

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "");

    // Generate and Download File
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const excelBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(excelBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.xlsx`;
    document.body.appendChild(a);

    setTimeout(() => {
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExcelLoading(false);
    }, 2000);
  };

  useEffect(() => {
    const businessId = sessionStorage.getItem("selectedBusinessId");

    if (activeBusinessInfo) dispatch(getCatalogReportsData(activeBusinessInfo));
    if (!ondcCategory) dispatch(getONDCCategories(businessId));
  }, [activeBusinessInfo]);

  useEffect(() => {
    if (ondcCategory) {
      const ondcFilteredCategory = ondcCategory?.map(
        ({ _id, productSubCategory }) => ({
          ondcCode: _id?.ondcCode,
          ondcValue: _id?.ondcValue,
          productSubCategory,
        })
      );

      formatCategory(ondcFilteredCategory, setOndcCategoryList, "ondcValue");
      console.log("ondcCategory", ondcCategory, ondcFilteredCategory);
    }
  }, [ondcCategory]);

  return (
    <Stack
      sx={{
        mt: "20px",
        gap: "10px",
      }}
    >
      <Box
        display="flex"
        sx={{
          maxWidth: "fit-content",
          borderBottom: "0px solid #e7e8eb",
          borderRadius: "7px",
          background: whiteColor,
          padding: "8px 12px",
          gap: 2,
        }}
      >
        <Button
          onClick={() => handleTabChange("priceAnalysis")}
          sx={{
            height: "35px",
            color: activeTab === "priceAnalysis" ? whiteColor : blackColor,
            background:
              activeTab === "priceAnalysis" ? blueColor04 : "transparent",
            borderRadius: "5px",
            border: "none",
            fontSize: "13px",
            fontWeight: 600,
            px: "15px",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: blueColor04,
              color: whiteColor,
            },
          }}
        >
          Price Analysis
        </Button>
        <Button
          onClick={() => handleTabChange("details")}
          sx={{
            height: "35px",
            px: "15px",
            color: activeTab === "inventoryAnalysis" ? whiteColor : blackColor,
            background:
              activeTab === "inventoryAnalysis" ? blueColor04 : "transparent",
            borderRadius: "5px",
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: blueColor04,
              color: whiteColor,
            },
          }}
        >
          Inventory Analysis
        </Button>
      </Box>

      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Typography
            fontSize={15}
            fontWeight={600}
            color={blackShade19}
            lineHeight="20px"
            py={1}
          >
            Inventory Analysis
          </Typography>
          <Box display="flex" gap={2}>
            <Box
              position={{ md: "relative", xs: "static" }}
              ref={ondcFilterRef}
            >
              <Button
                sx={styles.filterIconBtn}
                variant="contained"
                disableElevation
                onClick={() => {
                  // clearSearch();
                  // setIsClearFilter(null);
                  setShowONDCFilter((prev) => !prev);
                }}
                endIcon={<FilterListRoundedIcon sx={{ fontSize: "17px" }} />}
              >
                ONDC Category Filters
              </Button>
              {showONDCFilter && (
                <FilterCard
                  title="ONDC Category Filters"
                  categoryList={ondcCategoryList}
                  subCategoryList={ondcSubCatList}
                  selectedCategory={selectedOndcCategory}
                  selectedSubCategory={selectedOndcSubCat}
                  handleCategoryChange={handleOndcCategoryChange}
                  handleSubCategoryChange={handleOndcSubCategoryChange}
                  onApply={handleOndcFilter}
                  onCancel={onOndcFilterClose}
                  onAllClear={clearAllOndcFilterData}
                  error={filterError}
                />
              )}
            </Box>

            <Button
              sx={styles.actionBtn}
              startIcon={<DescriptionIcon />}
              onClick={onExcelDownload}
              disabled={excelLoading}
            >
              Excel Download
            </Button>
          </Box>
        </Box>

        {filterMessage && (
          <Box
            display="flex"
            alignItems="center"
            gap="6px"
            mt={1}
            mb={2}
            flexWrap={"wrap"}
          >
            <Typography
              component="h4"
              fontSize={12}
              color={blackShade05}
              fontWeight={500}
              lineHeight="20px"
            >
              {filterMessage?.message}
            </Typography>

            <Chip
              label={filterMessage?.category}
              sx={{ fontSize: "12px", height: "28px" }}
              onDelete={clearFilter}
            />
          </Box>
        )}

        {catalogDataLoader ? (
          <ThreeDotLoader />
        ) : (
          <InventoryComparisonTable catalogData={catalogData} />
        )}
      </Box>
    </Stack>
  );
}

export default InventoryAnalysisContent;

const styles = {
  filterIconBtn: {
    padding: "4px 12px",
    fontSize: "11px",
    fontWeight: 600,
    color: greyColor9,
    backgroundColor: whiteColor,
    border: "1px solid " + greyColor9,
    "&:hover": {
      color: whiteColor,
      backgroundColor: greyColor9,
    },
  },

  actionBtn: {
    fontSize: 11,
    fontWeight: 600,
    lineHeight: "22px",
    color: greenShade16,
    border: `1px solid ${greenShade16}`,
    backgroundColor: whiteColor,
    p: "2px 8px",
    "&:hover": {
      backgroundColor: greenShade16,
      color: whiteColor,
    },
    "&:disabled": {
      border: `1px solid ${greyColor33}`,
      opacity: 0.6,
    },
  },
};
